import React from "react"
import {useTranslation} from "react-i18next"
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import {setPageSize} from "../../../components/RowCountPicker.tsx"
import {KLARA_LEADS_PAGE_SIZE_KEY} from "../filters.tsx"

const PAGE_SIZES = [5, 10, 25]
/** Page buttons around the current page, besides the first and the last. */
const NEIGHBOUR_PAGES = 1

/** The page numbers to offer: the first, the last and the current page's neighbours; null marks a gap. */
export const visiblePages = (page: number, pageCount: number): Array<number | null> => {
  const candidates = [1, pageCount]
  for (let offset = -NEIGHBOUR_PAGES; offset <= NEIGHBOUR_PAGES; offset++) {
    candidates.push(page + offset)
  }
  const pages = Array.from(new Set(candidates.filter(candidate => candidate >= 1 && candidate <= pageCount))).sort(
    (a, b) => a - b
  )

  return pages.flatMap((candidate, index) =>
    index > 0 && candidate - pages[index - 1] > 1 ? [null, candidate] : [candidate]
  )
}

const NAV_BUTTON_CLASS_NAME =
  "flex h-[30px] min-w-[30px] items-center justify-center rounded-lg border px-2 text-xs font-semibold transition-colors"

/** "1–10 of 42 leads", the rows per page and the page buttons; hidden without leads. */
export const LeadsPagination: React.FC<{
  page: number
  setPage: (page: number) => void
  pageSize: number
  total: number | undefined
}> = ({page, setPage, pageSize, total}) => {
  const {t} = useTranslation()

  if (!total) {
    return null
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  // A remembered size outside the offered ones stays selectable.
  const sizes = Array.from(new Set([...PAGE_SIZES, pageSize])).sort((a, b) => a - b)

  return (
    <div className={"flex flex-wrap items-center gap-3 px-1 pt-3 pb-0.5 text-xs text-cr-slate-60"}>
      <span className={"whitespace-nowrap"}>
        {t("KlaraDashboard_Leads_PageRange", {
          from: (page - 1) * pageSize + 1,
          to: Math.min(page * pageSize, total),
          count: total,
        })}
      </span>
      <span className={"ml-3 flex items-center gap-1.5 whitespace-nowrap"}>
        {t("KlaraDashboard_Leads_RowsPerPage")}
        <span
          role={"group"}
          aria-label={t("KlaraDashboard_Leads_RowsPerPage")}
          className={"flex gap-0.5 rounded-lg bg-cr-slate-6 p-0.5"}
        >
          {sizes.map(size => (
            <button
              key={size}
              type={"button"}
              aria-pressed={size === pageSize}
              onClick={() => setPageSize(KLARA_LEADS_PAGE_SIZE_KEY, size)}
              className={twMerge(
                "rounded-md px-[9px] py-[3px] text-[11.5px] font-semibold text-cr-slate-60 transition-colors hover:text-cr-blue-dark",
                size === pageSize && "bg-cr-white text-cr-blue-dark shadow-xs"
              )}
            >
              {size}
            </button>
          ))}
        </span>
      </span>
      <nav aria-label={t("KlaraDashboard_Leads_Pages")} className={"ml-auto flex items-center gap-1"}>
        <button
          type={"button"}
          aria-label={t("T_Previous")}
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className={twMerge(
            NAV_BUTTON_CLASS_NAME,
            "border-cr-slate-15 bg-cr-white px-0 text-cr-grey-80 disabled:text-cr-grey-15"
          )}
        >
          <ChevronLeftIcon className={"size-3.5"} aria-hidden={"true"} />
        </button>
        {visiblePages(page, pageCount).map((candidate, index) =>
          candidate === null ? (
            <span key={`gap ${index}`} aria-hidden={"true"} className={"px-1"}>
              …
            </span>
          ) : (
            <button
              key={candidate}
              type={"button"}
              aria-current={candidate === page ? "page" : undefined}
              onClick={() => setPage(candidate)}
              className={twMerge(
                NAV_BUTTON_CLASS_NAME,
                candidate === page
                  ? "border-cr-blue bg-cr-blue text-cr-white"
                  : "border-cr-slate-15 bg-cr-white text-cr-grey-80 hover:border-cr-blue-mid"
              )}
            >
              {candidate}
            </button>
          )
        )}
        <button
          type={"button"}
          aria-label={t("T_Next")}
          disabled={page >= pageCount}
          onClick={() => setPage(page + 1)}
          className={twMerge(
            NAV_BUTTON_CLASS_NAME,
            "border-cr-slate-15 bg-cr-white px-0 text-cr-grey-80 disabled:text-cr-grey-15"
          )}
        >
          <ChevronRightIcon className={"size-3.5"} aria-hidden={"true"} />
        </button>
      </nav>
    </div>
  )
}
